import { useState } from 'react';
import {
  createAsset,
  deleteAsset,
  fetchAssets,
  patchAsset,
  AssetCommandError,
  type Asset,
  type AssetDraft,
} from '../lib/api/assets';
import {
  createAssetBooking,
  deleteAssetBooking,
  dropLocalAssetBookingsAttachedTo,
  fetchAssetBookings,
  patchAssetBooking,
  type AssetBooking,
  type AssetBookingIntent,
} from '../lib/api/assetBookings';
import { errMsg } from '../lib/errors';
import {
  createMemberAssetBooking,
  type MemberAssetBookingIntent,
  type MemberAssetBookingResult,
} from '../lib/api/memberAssetBookings';
import { playerSurfaceActive } from '../lib/api/customerContext';

// ─────────────────────────────────────────────────────────────────────
// useAssets — equipment beside the courts (docs/domains/ASSET_BOOKING.md).
//
// Two slices, both SERVER-AUTHORITATIVE on the http backend: the registry of
// assets this organization keeps or owns, and the asset bookings in this
// venue's calendar. Every write is a narrow command; the state adopts what
// the server answered rather than what the client sent, which is how a
// server-computed price or a refused overlap reaches the screen.
//
// On the memory backend (tests, no API) the api layer keeps the two blobs
// itself and runs the same engine, so the hook does not care which it is on.
//
// Loaded by the atomic mount loader (useInitialLoad) through the exposed
// setters, like every other club slice. Not part of the member bootstrap yet:
// the member surface is slice 2.
// ─────────────────────────────────────────────────────────────────────

export interface AssetMutationResult<T = unknown> {
  success: boolean;
  /** A CODE when the server refused (translated by the screen), else a message. */
  error?: string;
  code?: string | null;
  detail?: Record<string, unknown> | null;
  value?: T;
}

/** The result shape every writer here returns — one way to read a refusal. */
function refused(e: unknown): AssetMutationResult<never> {
  if (e instanceof AssetCommandError) {
    return { success: false, error: e.code, code: e.code, detail: e.detail };
  }
  return { success: false, error: errMsg(e), code: null };
}

export function useAssets() {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetBookings, setAssetBookings] = useState<AssetBooking[]>([]);

  /** Re-read both lists from the server — after a command that changed rows elsewhere. */
  const refreshAssets = async () => {
    const [a, b] = await Promise.all([fetchAssets(), fetchAssetBookings()]);
    setAssets(a);
    setAssetBookings(b);
  };

  // ── The registry ──────────────────────────────────────────────────

  const saveAsset = async (
    draft: AssetDraft & { id?: string },
  ): Promise<AssetMutationResult<Asset>> => {
    try {
      const { id, ...rest } = draft;
      const saved = id ? await patchAsset(id, rest) : await createAsset(rest);
      setAssets((list) => {
        const idx = list.findIndex((a) => a.id === saved.id);
        if (idx < 0) return [...list, saved];
        const out = [...list];
        out[idx] = saved;
        return out;
      });
      return { success: true, value: saved };
    } catch (e) {
      return refused(e);
    }
  };

  const removeAsset = async (id: string): Promise<AssetMutationResult> => {
    try {
      await deleteAsset(id);
      setAssets((list) => list.filter((a) => a.id !== id));
      return { success: true };
    } catch (e) {
      return refused(e);
    }
  };

  // ── The bookings ──────────────────────────────────────────────────

  /** What the memory backend's validator needs; the server reads its own tables. */
  const localContext = (cfg: Record<string, unknown> | null) => ({ assets, cfg });

  const addAssetBooking = async (
    intent: AssetBookingIntent,
    cfg: Record<string, unknown> | null = null,
  ): Promise<AssetMutationResult<AssetBooking>> => {
    try {
      const created = await createAssetBooking(intent, localContext(cfg));
      setAssetBookings((list) => [...list, created]);
      return { success: true, value: created };
    } catch (e) {
      return refused(e);
    }
  };

  const updateAssetBooking = async (
    id: string,
    patch: Partial<AssetBookingIntent>,
    cfg: Record<string, unknown> | null = null,
  ): Promise<AssetMutationResult<AssetBooking>> => {
    try {
      const saved = await patchAssetBooking(id, patch, localContext(cfg));
      setAssetBookings((list) => list.map((b) => (b.id === id ? saved : b)));
      return { success: true, value: saved };
    } catch (e) {
      return refused(e);
    }
  };

  const removeAssetBooking = async (id: string): Promise<AssetMutationResult> => {
    try {
      await deleteAssetBooking(id);
      setAssetBookings((list) => list.filter((b) => b.id !== id));
      return { success: true };
    } catch (e) {
      return refused(e);
    }
  };

  /**
   * The court bookings named were just deleted. On the API the database took
   * their equipment with them (ON DELETE CASCADE); the screen's copy — and the
   * memory backend's blob — follow here.
   */
  const dropAssetBookingsForBookings = async (bookingIds: readonly string[]) => {
    const ids = new Set(bookingIds.map(String));
    await dropLocalAssetBookingsAttachedTo(bookingIds);
    setAssetBookings((list) => list.filter((b) => !b.bookingId || !ids.has(String(b.bookingId))));
  };

  // ── The member path (docs/domains/ASSET_BOOKING.md, slice 2) ──────

  /**
   * Rows the SERVER created alongside something else — the equipment booked
   * with a member's court booking — adopted by id, so a replay or a refresh
   * never shows a machine twice.
   */
  const adoptAssetBookings = (rows: readonly AssetBooking[]) => {
    if (rows.length === 0) return;
    setAssetBookings((list) => {
      const ids = new Set(rows.map((r) => r.id));
      return [...list.filter((b) => !ids.has(b.id)), ...rows];
    });
  };

  /**
   * A MEMBER BOOKS EQUIPMENT ON ITS OWN. On the player surface this is the
   * member command — the server derives the price, holds the machine for
   * fifteen minutes and answers with the row; off it (the portal's preview of
   * the app, the memory backend) it is the staff command, exactly as
   * `useBookingActions.addBooking` splits the court path.
   */
  const addMemberAssetBooking = async (
    intent: MemberAssetBookingIntent,
    cfg: Record<string, unknown> | null = null,
  ): Promise<AssetMutationResult<AssetBooking> & { expiresInMs?: number }> => {
    if (!playerSurfaceActive()) {
      return addAssetBooking(
        {
          assetId: intent.assetId,
          date: intent.date,
          startTime: intent.startTime,
          durationMin: intent.durationMin,
        },
        cfg,
      );
    }
    let out: MemberAssetBookingResult;
    try {
      out = await createMemberAssetBooking(intent);
    } catch (e) {
      return refused(e);
    }
    if (!out.success || !out.assetBooking) {
      return { success: false, error: out.error ?? out.code, code: out.code ?? null };
    }
    const created = out.assetBooking as unknown as AssetBooking;
    adoptAssetBookings([created]);
    return {
      success: true,
      value: created,
      ...(out.expiresInMs === undefined ? {} : { expiresInMs: out.expiresInMs }),
    };
  };

  return {
    assets,
    assetBookings,
    // For the atomic mount loader and the member bootstrap.
    setAssets,
    setAssetBookings,
    refreshAssets,
    saveAsset,
    removeAsset,
    addAssetBooking,
    updateAssetBooking,
    removeAssetBooking,
    dropAssetBookingsForBookings,
    adoptAssetBookings,
    addMemberAssetBooking,
  };
}
